/*
 * Project: Goalscape
 * File:    firebaseUtils.js
 * Path:    src/utils/firebaseUtils.js
 * Description: Utility functions for Firebase operations and user data management.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement robust error handling and logging for all Firebase operations. Consider using try-catch blocks with specific error types.
 */
import { doc, updateDoc, getDoc, setDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

/*
 * Function: initializeProjectOrder
 * Description: Initializes or retrieves the project order for a user or workspace.
 * Parameters:
 *   orderDocRef: DocumentReference - The Firestore document reference for the order.
 * Returns: Promise<string[]> - Array of project IDs in their order.
 * Usage:
 *   const order = await initializeProjectOrder(orderDocRef);
 * AI Hint: Consider using server-side functions or triggers to automatically initialize project order.
 */
export const initializeProjectOrder = async (orderDocRef) => {
    const orderDoc = await getDoc(orderDocRef);
    if (!orderDoc.exists()) {
        await setDoc(orderDocRef, { order: [] });
        return [];
    }
    return orderDoc.data().order;
};

/*
 * Function: updateProjectOrder
 * Description: Updates the order of projects.
 * Parameters:
 *   orderDocRef: DocumentReference - The Firestore document reference for the order.
 *   newOrder: string[] - Array of project IDs in their new order.
 * Returns: Promise<void>
 * Usage:
 *   await updateProjectOrder(orderDocRef, ['project1', 'project2']);
 * AI Hint: Implement optimistic updates to improve UI responsiveness when reordering projects.
 */
export const updateProjectOrder = async (orderDocRef, newOrder) => {
    try {
        await updateDoc(orderDocRef, {
            order: newOrder,
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error updating project order:', error);
        throw error;
    }
};

/*
 * Function: getWorkspaceProjects
 * Description: Fetches all projects within a given workspace.
 * Parameters:
 *   workspaceId: string - The ID of the workspace.
 * Returns: Promise<object[]> - Array of project objects.
 * AI Hint: Consider adding pagination or filtering if a workspace can have a very large number of projects.
 */
export const getWorkspaceProjects = async (workspaceId) => {
    if (!workspaceId) {
        console.error('Error: workspaceId is required to fetch projects.');
        return [];
    }
    try {
        const projectsRef = collection(db, 'projects');
        const q = query(projectsRef, where('workspaceId', '==', workspaceId));
        const querySnapshot = await getDocs(q);
        const projects = [];
        querySnapshot.forEach((doc) => {
            projects.push({ id: doc.id, ...doc.data() });
        });
        return projects;
    } catch (error) {
        console.error('Error fetching workspace projects:', error);
        throw error;
    }
};

/*
 * Function: moveCardToProject
 * Description: Moves a card from a source project/column to a target project/column.
 * Parameters:
 *   cardData: object - The card object to move.
 *   sourceProjectId: string - The ID of the source project.
 *   sourceColumnId: string - The ID of the source column.
 *   targetProjectId: string - The ID of the target project.
 *   targetColumnId: string - The ID of the target column.
 * Returns: Promise<void>
 * AI Hint: Ensure this operation is atomic or handle potential inconsistencies if one part fails.
 *          Consider what happens if label IDs or assignee IDs are not valid in the target project.
 */
export const moveCardToProject = async (
    cardData,
    sourceProjectId,
    sourceColumnId,
    targetProjectId,
    targetColumnId
) => {
    if (!cardData || !sourceProjectId || !sourceColumnId || !targetProjectId || !targetColumnId) {
        console.error('Error: Missing parameters for moveCardToProject.');
        throw new Error('Missing parameters for moveCardToProject.');
    }

    const batch = writeBatch(db);

    // 1. Remove card from source project
    const sourceProjectRef = doc(db, 'projects', sourceProjectId);
    const sourceProjectSnap = await getDoc(sourceProjectRef);
    if (!sourceProjectSnap.exists()) {
        throw new Error(`Source project ${sourceProjectId} not found.`);
    }
    const sourceProjectData = sourceProjectSnap.data();
    const sourceColumn = sourceProjectData.columns[sourceColumnId];
    if (!sourceColumn) {
        throw new Error(`Source column ${sourceColumnId} not found in project ${sourceProjectId}.`);
    }
    const updatedSourceCards = sourceColumn.cards.filter(card => card.id !== cardData.id);
    batch.update(sourceProjectRef, {
        [`columns.${sourceColumnId}.cards`]: updatedSourceCards,
        updatedAt: new Date().toISOString()
    });

    // 2. Add card to target project
    const targetProjectRef = doc(db, 'projects', targetProjectId);
    const targetProjectSnap = await getDoc(targetProjectRef);
    if (!targetProjectSnap.exists()) {
        throw new Error(`Target project ${targetProjectId} not found.`);
    }
    const targetProjectData = targetProjectSnap.data();
    const targetColumn = targetProjectData.columns[targetColumnId];
    if (!targetColumn) {
        throw new Error(`Target column ${targetColumnId} not found in project ${targetProjectId}.`);
    }
    const updatedTargetCards = [...(targetColumn.cards || []), cardData]; // Add the card
    batch.update(targetProjectRef, {
        [`columns.${targetColumnId}.cards`]: updatedTargetCards,
        updatedAt: new Date().toISOString()
    });

    try {
        await batch.commit();
    } catch (error) {
        console.error('Error moving card to project:', error);
        throw error;
    }
};

/*
 * Function: initializeUserTodos
 * Description: Initializes or retrieves a user's todos collection.
 * Parameters:
 *   userId: string - The ID of the user.
 * Returns: Promise<any[]> - Array of todo items.
 * Usage:
 *   const todos = await initializeUserTodos('user123');
 * AI Hint: Consider using a subcollection for todos to improve scalability and query performance.
 */
export const initializeUserTodos = async (userId) => {
    try {
        const userTodosRef = doc(db, 'userTodos', userId);
        const todosDoc = await getDoc(userTodosRef);

        if (!todosDoc.exists()) {
            await setDoc(userTodosRef, {
                items: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
            return [];
        }

        return todosDoc.data().items;
    } catch (error) {
        console.error('Error initializing user todos:', error);
        throw error;
    }
};

/*
 * Function: updateUserTodos
 * Description: Updates a user's todos collection.
 * Parameters:
 *   userId: string - The ID of the user.
 *   newTodos: any[] - Array of updated todo items.
 * Returns: Promise<void>
 * Usage:
 *   await updateUserTodos('user123', updatedTodos);
 * AI Hint: Implement pagination for large todo collections to improve performance.
 */
export const updateUserTodos = async (userId, newTodos) => {
    try {
        const userTodosRef = doc(db, 'userTodos', userId);
        await updateDoc(userTodosRef, {
            items: newTodos,
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error updating user todos:', error);
        throw error;
    }
};

/*
 * Function: initializeUserProjectSettings
 * Description: Initializes or retrieves a user's project settings.
 * Parameters:
 *   userId: string - The ID of the user.
 * Returns: Promise<object> - The user's project settings.
 * Usage:
 *   const settings = await initializeUserProjectSettings('user123');
 * AI Hint: Consider using a separate document for each user's settings to improve scalability.
 */
export const initializeUserProjectSettings = async (userId) => {
    try {
        const userSettingsRef = doc(db, 'userSettings', userId);
        const settingsDoc = await getDoc(userSettingsRef);

        if (!settingsDoc.exists()) {
            const initialSettings = {
                starredProjects: [],
                background: null,
                ideasPlanBackground: null, // Add new field
                focus: [], // Initialize as an empty array
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            await setDoc(userSettingsRef, initialSettings);
            return {
                starredProjects: [],
                background: null,
                ideasPlanBackground: null, // Return new field
                focus: []
            };
        }

        const data = settingsDoc.data();
        // Ensure ideasPlanBackground exists, if not, initialize it
        if (data.ideasPlanBackground === undefined) {
            data.ideasPlanBackground = null;
        }
        // Ensure focus is always an array and migrate old string format
        if (data.focus) {
            if (Array.isArray(data.focus)) {
                if (data.focus.length > 0 && typeof data.focus[0] === 'string') {
                    // Old format: array of strings, convert to new object format
                    data.focus = data.focus.map(itemText => ({
                        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        text: itemText,
                        dueDate: null,
                    }));
                } else {
                    // It's an array of objects, check dueDate format
                    data.focus = data.focus.map(item => {
                        if (item.dueDate && typeof item.dueDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(item.dueDate)) {
                            return { ...item, dueDate: `${item.dueDate}T00:00` };
                        }
                        return item;
                    });
                }
            } else if (typeof data.focus === 'string') {
                // Very old format: single string, convert to new object format in an array
                data.focus = [{
                    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    text: data.focus,
                    dueDate: null, // No specific due date to convert here
                }];
            } else {
                // If it's some other unexpected type, reset to empty array
                data.focus = [];
            }
        } else {
            // If focus doesn't exist or is null/undefined, initialize as empty array
            data.focus = [];
        }
        return data;
    } catch (error) {
        console.error('Error initializing user settings:', error);
        throw error;
    }
};

/*
 * Function: updateUserSettings
 * Description: Updates a user's project settings.
 * Parameters:
 *   userId: string - The ID of the user.
 *   settings: object - The settings to update.
 * Returns: Promise<void>
 * Usage:
 *   await updateUserSettings('user123', { background: 'blue' });
 * AI Hint: Implement server-side validation for user settings to prevent invalid data.
 */
export const updateUserSettings = async (userId, settings) => {
    try {
        const userSettingsRef = doc(db, 'userSettings', userId);
        const updatedSettings = { ...settings };

        // Ensure focus is always an array before updating
        if (updatedSettings.focus !== undefined) {
            if (typeof updatedSettings.focus === 'string') {
                updatedSettings.focus = [updatedSettings.focus];
            } else if (!Array.isArray(updatedSettings.focus)) {
                updatedSettings.focus = [];
            }
        }

        await updateDoc(userSettingsRef, {
            ...updatedSettings,
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error updating user settings:', error);
        throw error;
    }
};

/*
 * Function: toggleProjectStarStatus
 * Description: Toggles whether a project is starred by a user.
 * Parameters:
 *   userId: string - The ID of the user.
 *   projectId: string - The ID of the project.
 *   isCurrentlyStarred: boolean - Whether the project is currently starred.
 * Returns: Promise<void>
 * Usage:
 *   await toggleProjectStarStatus('user123', 'project456', false);
 * AI Hint: Consider using a more efficient data structure for storing starred projects (e.g., a set).
 */
export const toggleProjectStarStatus = async (userId, projectId, isCurrentlyStarred) => {
    try {
        const userSettingsRef = doc(db, 'userSettings', userId);
        const settingsDoc = await getDoc(userSettingsRef);

        let starredProjects = settingsDoc.exists()
            ? (settingsDoc.data().starredProjects || [])
            : [];

        if (isCurrentlyStarred) {
            starredProjects = starredProjects.filter(id => id !== projectId);
        } else {
            starredProjects = [...starredProjects, projectId];
        }

        await updateDoc(userSettingsRef, {
            starredProjects,
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error toggling project star:', error);
        throw error;
    }
};
